import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [aiwaveTheme, setAiwaveTheme] = useState(true);
  const [theme, setTheme] = useState('system');
  const [mounted, setMounted] = useState(false);

  // Get the resolved theme (system preference or explicit choice)
  const getResolvedTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  useEffect(() => {
    setMounted(true);
    
    // Load theme preferences from localStorage
    const savedTheme = localStorage.getItem('aiwaveTheme');
    const savedColorTheme = localStorage.getItem('colorTheme');
    
    if (savedTheme !== null) {
      setAiwaveTheme(JSON.parse(savedTheme));
    }
    
    if (savedColorTheme) {
      setTheme(savedColorTheme);
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;
    
    // Save theme preferences to localStorage
    localStorage.setItem('aiwaveTheme', JSON.stringify(aiwaveTheme));
    localStorage.setItem('colorTheme', theme);
    
    // Apply themes to document
    document.documentElement.setAttribute('data-aiwave-theme', aiwaveTheme);
    document.documentElement.setAttribute('data-theme', getResolvedTheme());
  }, [aiwaveTheme, theme, mounted]);

  // Listen for system theme changes
  useEffect(() => {
    if (!mounted) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        document.documentElement.setAttribute('data-theme', getResolvedTheme());
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, mounted]);

  const toggleAiwaveTheme = () => {
    setAiwaveTheme(prev => !prev);
  };

  const toggleColorTheme = () => {
    setTheme(prev => {
      if (prev === 'dark') return 'light';
      if (prev === 'light') return 'system';
      return 'dark';
    });
  };

  const setColorTheme = (newTheme) => {
    setTheme(newTheme);
  };

  const value = {
    aiwaveTheme,
    setAiwaveTheme,
    toggleAiwaveTheme,
    theme,
    setColorTheme,
    toggleColorTheme,
    resolvedTheme: getResolvedTheme(),
    mounted
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 